import React, { useEffect, useRef, forwardRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Sidebar } from "../sidebar/Sidebar";
import { CloseButton } from "../input/CloseButton";
import { ReactComponent as WandIcon } from "../icons/SpawnObject.svg";
import { ReactComponent as AttachIcon } from "../icons/Attach.svg";
import { ReactComponent as ChatIcon } from "../icons/Chat.svg";
import { ReactComponent as SendIcon } from "../icons/Send.svg";
import { ReactComponent as ReactionIcon } from "../icons/Reaction.svg";
import { IconButton } from "../input/IconButton";
import { TextAreaInput } from "../input/TextAreaInput";
import { ToolbarButton } from "../input/ToolbarButton";
import { Popover } from "../popover/Popover";
import { EmojiPicker } from "./EmojiPicker";
import styles from "./ChatSidebar.scss";
import { formatMessageBody } from "../../utils/chat-message";
import { formatAIMessageBody } from "../../utils/chat-ai-message";
import { FormattedMessage, useIntl, defineMessages, FormattedRelativeTime } from "react-intl";
import { permissionMessage } from "./PermissionNotifications";
import detectMobile from "../../utils/is-mobile";
import { proxiedUrlFor } from "../../utils/media-url-utils";
import { upload } from "../../utils/media-utils";

import stopUrl from "../../assets/images/sprites/action/stop-action.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay } from "@fortawesome/free-solid-svg-icons/faPlay";
import { PlayFilled } from "@fluentui/react-icons";

export function SpawnMessageButton(props) {
  return (
    <IconButton className={styles.chatInputIcon} {...props}>
      <WandIcon width={22} height={22} />
    </IconButton>
  );
}

export function SendMessageButton(props) {
  return (
    <IconButton className={classNames(styles.chatInputIcon, styles.sendMessage)} {...props}>
      <SendIcon width={20} height={20} />
    </IconButton>
  );
}

// Memoize EmojiPickerPopoverButton since we don't want it re-rendering
// the EmojiPicker unnecessarily.
export const EmojiPickerPopoverButton = React.memo(({ onSelectEmoji, disabled }) => {
  // We're using a ref here, since we don't want to re-render anything, but we
  // do want to know if the Shift key is down when an emoji is selected.
  const shiftKeyDown = useRef(false);

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === "Shift") shiftKeyDown.current = true;
    };
    const onKeyUp = e => {
      if (e.key === "Shift") shiftKeyDown.current = false;
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  // content={props => ((childProps) => <MoreMenuPopoverContent menu={menu} {...props} {...childProps} />)}
  return (
    <Popover
      title=""
      content={props => childProps =>
        (
          <EmojiPicker
            {...props}
            {...childProps}
            onSelect={emoji => {
              const keepPickerOpen = shiftKeyDown.current;
              onSelectEmoji({ emoji, pickerRemainedOpen: keepPickerOpen });
              // Keep the picker open if the Shift key was held down to allow
              // for multiple emoji selections.

              if (!keepPickerOpen && props?.closePopover) props.closePopover();
              if (!keepPickerOpen && childProps?.closePopover) childProps.closePopover();
            }}
          />
        )}
      placement="top"
      offsetDistance={46}
    >
      {({ togglePopover, popoverVisible, triggerRef }) => (
        <IconButton
          ref={triggerRef}
          className={styles.chatInputIcon}
          selected={popoverVisible}
          onClick={togglePopover}
          disabled={disabled}
        >
          <ReactionIcon />
        </IconButton>
      )}
    </Popover>
  );
});

EmojiPickerPopoverButton.propTypes = {
  onSelectEmoji: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

EmojiPickerPopoverButton.displayName = "EmojiPickerPopoverButton";

export function MessageAttachmentButton(props) {
  return (
    <>
      <IconButton as="label" className={styles.chatInputIcon} disabled={props.disabled}>
        <AttachIcon />
        <input type="file" {...props} disabled={props.disabled} />
      </IconButton>
    </>
  );
}

MessageAttachmentButton.propTypes = {
  disabled: PropTypes.bool
};

export function ChatLengthWarning({ messageLength, maxLength }) {
  return (
    <p
      className={classNames(styles.chatInputWarning, {
        [styles.warningTextColor]: messageLength > maxLength
      })}
    >
      <FormattedMessage id="chat-message-input.warning-max-characters" defaultMessage="Max characters" />
      {` (${messageLength}/${maxLength})`}
    </p>
  );
}

ChatLengthWarning.propTypes = {
  messageLength: PropTypes.number,
  maxLength: PropTypes.number
};

export const ChatInput = forwardRef(({ warning, isOverMaxLength, ...props }, ref) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const chatInputContainer = document.getElementById("chat-input-container");
    const chatInput = chatInputContainer.querySelector("textarea");
    const onFocus = () => {
      chatInputContainer.classList.add("keyboard");

      setTimeout(() => {
        setIsOpen(true);
      }, 500);
    };
    const handleResize = () => {
      if (isOpen) {
        chatInputContainer.classList.remove("keyboard");
        setIsOpen(false);
      }
    };

    const handleBlur = () => {
      setIsOpen(false);
      chatInputContainer.classList.remove("keyboard");
    };
    if (detectMobile()) {
      chatInput.addEventListener("click", onFocus);
      chatInput.addEventListener("blur", handleBlur);
      window.addEventListener("resize", handleResize);

      return () => {
        chatInput.removeEventListener("click", onFocus);
        chatInput.removeEventListener("blur", handleBlur);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isOpen]);

  return (
    <div id="chat-input-container" className={styles.chatInputContainer}>
      <TextAreaInput
        ref={ref}
        textInputStyles={styles.chatInputTextAreaStyles}
        className={classNames({ [styles.warningBorder]: isOverMaxLength }, styles.chatInput)}
        placeholder={intl.formatMessage({ id: "chat-sidebar.input.placeholder", defaultMessage: "Message..." })}
        {...props}
      />
      {warning}
    </div>
  );
});

ChatInput.propTypes = {
  onSpawn: PropTypes.func,
  warning: PropTypes.node,
  isOverMaxLength: PropTypes.bool
};

ChatInput.displayName = "ChatInput";

const enteredMessages = defineMessages({
  room: { id: "chat-sidebar.system-message.entered-room", defaultMessage: "{name} entered the Verse." },
  lobby: { id: "chat-sidebar.system-message.entered-lobby", defaultMessage: "{name} entered the lobby." }
});

const joinedMessages = defineMessages({
  lobby: { id: "chat-sidebar.system-message.joined-lobby", defaultMessage: "{name} joined the lobby." },
  room: { id: "chat-sidebar.system-message.joined-room", defaultMessage: "{name} joined the Verse." }
});

const LogMessageType = {
  roomEntryRequired: "roomEntryRequired",
  flyModeDisabled: "flyModeDisabled",
  flyModeEnabled: "flyModeEnabled",
  unauthorizedSceneChange: "unauthorizedSceneChange",
  invalidSceneUrl: "invalidSceneUrl",
  unauthorizedRoomRename: "unauthorizedRoomRename",
  captureUnavailable: "captureUnavailable",
  captureStopped: "captureStopped",
  captureStarted: "captureStarted",
  captureAlreadyStopped: "captureAlreadyStopped",
  captureAlreadyRunning: "captureAlreadyRunning",
  positionalAudioEnabled: "positionalAudioEnabled",
  positionalAudioDisabled: "positionalAudioDisabled",
  setAudioNormalizationFactor: "setAudioNormalizationFactor",
  audioNormalizationDisabled: "audioNormalizationDisabled",
  audioNormalizationNaN: "audioNormalizationNaN",
  invalidAudioNormalizationRange: "invalidAudioNormalizationRange",
  audioSuspended: "audioSuspended",
  audioResumed: "audioResumed",
  joinFailed: "joinFailed"
};

const logMessages = defineMessages({
  [LogMessageType.roomEntryRequired]: {
    id: "chat-sidebar.log-message.room-entry-required",
    defaultMessage: "You must enter the Verse to use this command."
  },
  [LogMessageType.flyModeDisabled]: {
    id: "chat-sidebar.log-message.fly-mode-disabled",
    defaultMessage: "Fly mode disabled."
  },
  [LogMessageType.flyModeEnabled]: {
    id: "chat-sidebar.log-message.fly-mode-enabled",
    defaultMessage: "Fly mode enabled."
  },
  [LogMessageType.unauthorizedSceneChange]: {
    id: "chat-sidebar.log-message.unauthorized-scene-change",
    defaultMessage: "You do not have permission to change the scene."
  },
  [LogMessageType.invalidSceneUrl]: {
    id: "chat-sidebar.log-message.invalid-scene-url",
    defaultMessage: "This URL does not point to a scene or valid GLB."
  },
  [LogMessageType.unauthorizedRoomRename]: {
    id: "chat-sidebar.log-message.unauthorized-room-rename",
    defaultMessage: "You do not have permission to rename this room."
  },
  [LogMessageType.captureUnavailable]: {
    id: "chat-sidebar.log-message.capture-unavailable",
    defaultMessage: "Capture unavailable."
  },
  [LogMessageType.captureStopped]: {
    id: "chat-sidebar.log-message.capture-stopped",
    defaultMessage: "Capture stopped."
  },
  [LogMessageType.captureStarted]: {
    id: "chat-sidebar.log-message.capture-started",
    defaultMessage: "Capture started."
  },
  [LogMessageType.captureAlreadyStopped]: {
    id: "chat-sidebar.log-message.capture-already-stopped",
    defaultMessage: "Capture already stopped."
  },
  [LogMessageType.captureAlreadyRunning]: {
    id: "chat-sidebar.log-message.capture-already-running",
    defaultMessage: "Capture already running."
  },
  [LogMessageType.positionalAudioEnabled]: {
    id: "chat-sidebar.log-message.positional-audio-enabled",
    defaultMessage: "Positional audio enabled."
  },
  [LogMessageType.positionalAudioDisabled]: {
    id: "chat-sidebar.log-message.positional-audio-disabled",
    defaultMessage: "Positional audio disabled."
  },
  [LogMessageType.setAudioNormalizationFactor]: {
    id: "chat-sidebar.log-message.set-audio-normalization-factor",
    defaultMessage: "audioNormalization factor is set to {factor}."
  },
  [LogMessageType.audioNormalizationDisabled]: {
    id: "chat-sidebar.log-message.audio-normalization-disabled",
    defaultMessage: "audioNormalization is disabled."
  },
  [LogMessageType.audioNormalizationNaN]: {
    id: "chat-sidebar.log-message.audio-normalization-nan",
    defaultMessage: "audioNormalization command needs a valid number parameter."
  },
  [LogMessageType.invalidAudioNormalizationRange]: {
    id: "chat-sidebar.log-message.invalid-audio-normalization-range",
    defaultMessage:
      "audioNormalization command needs a base volume number between 0 [no normalization] and 255. Default is 0. The recommended value is 4, if you would like to enable normalization."
  },
  [LogMessageType.audioSuspended]: {
    id: "chat-sidebar.log-message.audio-suspended",
    defaultMessage: "Audio has been suspended, click somewhere in the Verse to resume the audio."
  },
  [LogMessageType.audioResumed]: {
    id: "chat-sidebar.log-message.audio-resumed",
    defaultMessage: "Audio has been resumed."
  },
  [LogMessageType.joinFailed]: {
    id: "chat-sidebar.log-message.join-failed",
    defaultMessage: "Failed to join room: {message}"
  }
});

// TODO: use react-intl's defineMessages to get proper extraction
export function formatSystemMessage(entry, intl) {
  switch (entry.type) {
    case "join":
      return intl.formatMessage(joinedMessages[entry.presence], { name: <b>{entry.name}</b> });
    case "entered":
      return intl.formatMessage(enteredMessages[entry.presence], { name: <b>{entry.name}</b> });
    case "leave":
      return (
        <FormattedMessage
          id="chat-sidebar.system-message.leave"
          defaultMessage="{name} left."
          values={{ name: <b>{entry.name}</b> }}
        />
      );
    case "display_name_changed":
      return (
        <FormattedMessage
          id="chat-sidebar.system-message.name-change"
          defaultMessage="{oldName} is now known as {newName}"
          values={{ oldName: <b>{entry.oldName}</b>, newName: <b>{entry.newName}</b> }}
        />
      );
    case "scene_changed":
      return (
        <FormattedMessage
          id="chat-sidebar.system-message.scene-change"
          defaultMessage="{name} changed the scene to {sceneName}"
          values={{ name: <b>{entry.name}</b>, sceneName: <b>{entry.sceneName}</b> }}
        />
      );
    case "hub_name_changed":
      return (
        <FormattedMessage
          id="chat-sidebar.system-message.hub-name-change"
          defaultMessage="{name} changed the name of the Verse to {hubName}"
          values={{ name: <b>{entry.name}</b>, hubName: <b>{entry.hubName}</b> }}
        />
      );
    case "hub_changed":
      return (
        <FormattedMessage
          id="chat-sidebar.system-message.hub-change"
          defaultMessage="You are now in {hubName}"
          values={{ hubName: <b>{entry.hubName}</b> }}
        />
      );
    case "log":
      return intl.formatMessage(logMessages[entry.messageType], entry.props);
    default:
      return null;
  }
}

export function SystemMessage(props) {
  const intl = useIntl();

  return (
    <li className={classNames(styles.messageGroup, styles.systemMessage)}>
      {props.showLineBreak && <hr />}
      <p className={styles.messageGroupLabel}>
        <i>{formatSystemMessage(props, intl)}</i>
        <span>
          <FormattedRelativeTime updateIntervalInSeconds={10} value={(props.timestamp - Date.now()) / 1000} />
        </span>
      </p>
    </li>
  );
}

SystemMessage.propTypes = {
  timestamp: PropTypes.any,
  showLineBreak: PropTypes.bool
};

export const bubbletypes = ["none", "left", "middle", "right"];

function MessageBubble({ media, monospace, emoji, children, permission, loading }) {
  return (
    <div
      className={classNames(styles.messageBubble, {
        [styles.media]: media,
        [styles.emoji]: emoji,
        [styles.monospace]: monospace,
        [styles.permission]: permission,
        [styles.loading]: loading
      })}
    >
      {children}
    </div>
  );
}

MessageBubble.propTypes = {
  media: PropTypes.bool,
  monospace: PropTypes.bool,
  emoji: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  children: PropTypes.node,
  permission: PropTypes.bool,
  loading: PropTypes.bool
};

function getMessageComponent(message, intl, additionalProps) {
  switch (message.type) {
    case "chat": {
      const { formattedBody, monospace, emoji } = formatMessageBody(message.body);
      return (
        <MessageBubble key={message.id} monospace={monospace} emoji={emoji}>
          {formattedBody}
        </MessageBubble>
      );
    }
    case "private-chat": {
      const { formattedBody, monospace, emoji } = formatAIMessageBody(message.body);
      const cancelAllPlay = additionalProps?.cancelAllPlay;
      const playAudio = additionalProps?.playAudio;

      if (!message.play && (message.audio || message.playable)) {
        return (
          <div className={styles.aiMessageContainer}>
            <MessageBubble key={message.id} monospace={monospace} emoji={emoji}>
              {formattedBody}
            </MessageBubble>
            <SoundBarAnimation
              onClick={() => playAudio(message)}
              containerClassName={styles.chatSoundbar}
              play={false}
            ></SoundBarAnimation>
          </div>
        );
      }

      if (!message.play)
        return (
          <MessageBubble key={message.id} monospace={monospace} emoji={emoji}>
            {formattedBody}
          </MessageBubble>
        );

      return (
        <div className={styles.aiMessageContainer}>
          <MessageBubble key={message.id} monospace={monospace} emoji={emoji}>
            {formattedBody}
          </MessageBubble>
          {message.play && (
            <SoundBarAnimation
              play={message.play}
              containerClassName={styles.chatSoundbar}
              onClick={cancelAllPlay}
            ></SoundBarAnimation>
          )}
        </div>
      );
    }
    case "video":
      return (
        <MessageBubble key={message.id} media>
          <video controls src={message.body.src} />
        </MessageBubble>
      );
    case "image":
    case "photo":
      return (
        <MessageBubble key={message.id} media>
          <img src={message.body.src} />
        </MessageBubble>
      );
    case "audio":
      return (
        <MessageBubble key={message.id} media>
          <audio controls src={message.body.src} />
        </MessageBubble>
      );
    case "permission":
      return (
        <MessageBubble key={message.id} media>
          <img src={message.body.src} />
        </MessageBubble>
      );
    case "object":
      const url = new URL(message?.body?.src);
      const urlFilename = url?.pathname?.split("/")?.pop();
      const { formattedBody } = formatMessageBody(message?.body?.name || urlFilename);
      return (
        <MessageBubble key={message.id}>
          <p style={{ fontStyle: "italic" }}>{formattedBody}</p>
        </MessageBubble>
      );
    default:
      return null;
  }
}

export function ChatMessageGroup({ sent, sender, timestamp, messages, onSpawn, canSpawnMessages }) {
  const intl = useIntl();
  return (
    <li className={classNames(styles.messageGroup, { [styles.sent]: sent })}>
      <p className={styles.messageGroupLabel}>
        {sender} | <FormattedRelativeTime updateIntervalInSeconds={10} value={(timestamp - Date.now()) / 1000} />
      </p>
      <ul className={styles.messageGroupMessages}>
        {messages.map(message => {
          if (message?.body?.canSpawn && canSpawnMessages) {
            return (
              <div className={styles.spawnContainer}>
                <SpawnMessageButton onClick={() => onSpawn(message?.body?.src)} />
                {getMessageComponent(message, intl)}
              </div>
            );
          }
          return getMessageComponent(message, intl);
        })}
      </ul>
    </li>
  );
}

function DalleImageAI({ message, ...props }) {
  const proxiedUrlBody = proxiedUrlFor(message.body);
  const entry = {
    name: proxiedUrlBody,
    path: proxiedUrlBody,
    public_thumbnail: proxiedUrlBody,
    type: "image",
    thumbnail: proxiedUrlBody,
    url: proxiedUrlBody
  };

  async function urlToBlob(url) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    return blob;
  }

  const sendToScene = async () => {
    const file = await urlToBlob(proxiedUrlBody);
    const response = await upload(file, "image/png");

    window.APP.store.update({
      uploadPromotionTokens: [{ fileId: response.file_id, promotionToken: response.meta.promotion_token }]
    });

    entry.url = response.origin + `?token=${response.meta.access_token}`;

    window.APP.scene.emit("action_selected_media_result_entry", { entry, selectAction: "my-media" });
  };

  return (
    <div className={styles.imageAi}>
      <img src={proxiedUrlFor(message.body)}></img>
      <a href="#" onClick={sendToScene}>
        Open in Metaverse
      </a>
    </div>
  );
}

export function ChatMessageGroupWithLoading({ sent, sender, timestamp, messages, loading, cancelAllPlay, playAudio }) {
  const intl = useIntl();
  function processText(inputText) {
    if (!inputText) return inputText;

    // Regular expression to detect an image URL
    const imageUrlRegex = /(https?:\/\/.*\.(?:png|jpg|jpeg|gif|bmp|webp))/gi;

    // Replace the image URL with an <img> tag
    const processedText = inputText.replace(imageUrlRegex, '<img src="$1" alt="Image">');

    return processedText;
  }

  return (
    <li className={classNames(styles.messageGroup, { [styles.sent]: sent })}>
      <p className={styles.messageGroupLabel}>
        {sender} | <FormattedRelativeTime updateIntervalInSeconds={10} value={(timestamp - Date.now()) / 1000} />
      </p>
      {!loading && (
        <ul className={styles.messageGroupMessages}>
          {messages.map(message => {
            let bodyContent = message?.body?.message !== undefined ? message.body : message;
            let bodyMessage = bodyContent.message || message.body;

            if (bodyMessage && typeof bodyMessage === "string" && bodyMessage?.startsWith("https")) {
              return <DalleImageAI message={message}></DalleImageAI>;
            } else {
              return getMessageComponent(bodyContent, intl, { cancelAllPlay, playAudio });
            }
          })}
        </ul>
      )}
      {loading && (
        <ul className={styles.messageGroupMessages}>
          <MessageBubble monospace={true} loading={true}>
            <div class="snippet" data-title="dot-flashing">
              <div class="stage">
                <div class="dot-flashing"></div>
              </div>
            </div>
          </MessageBubble>
        </ul>
      )}
    </li>
  );
}

ChatMessageGroup.propTypes = {
  sent: PropTypes.bool,
  sender: PropTypes.string,
  timestamp: PropTypes.any,
  messages: PropTypes.array
};

export function PermissionMessageGroup({ sent, timestamp, messages }) {
  const intl = useIntl();
  return (
    <li className={classNames(styles.messageGroup, { [styles.sent]: sent })}>
      <p className={styles.messageGroupLabel}>
        <FormattedRelativeTime updateIntervalInSeconds={10} value={(timestamp - Date.now()) / 1000} />
      </p>
      <ul className={styles.messageGroupMessages}>
        {messages.map(message => (
          <MessageBubble key={message.id} permission>
            {permissionMessage(
              {
                permission: message.body.permission,
                status: message.body.status
              },
              intl
            )}
          </MessageBubble>
        ))}
      </ul>
    </li>
  );
}

PermissionMessageGroup.propTypes = {
  sent: PropTypes.bool,
  timestamp: PropTypes.any,
  messages: PropTypes.array
};

export const ChatMessageList = forwardRef(({ children, ...rest }, ref) => (
  <ul {...rest} className={styles.messageList} ref={ref}>
    {children}
  </ul>
));

ChatMessageList.propTypes = {
  children: PropTypes.node
};

ChatMessageList.displayName = "ChatMessageList";

export function ChatSidebar({
  title,
  onClose,
  children,
  subtitle,
  tabs,
  currentTab,
  changeTab,
  headerClassName,
  customTitle,
  beforeTitle,
  ...rest
}) {
  return (
    <Sidebar
      title={title}
      customTitle={customTitle}
      beforeTitle={beforeTitle}
      afterTitle={<CloseButton onClick={onClose} />}
      contentClassName={styles.content}
      disableOverflowScroll
      tabs={tabs}
      currentTab={currentTab}
      changeTab={changeTab}
      headerClassName={headerClassName}
      {...rest}
    >
      {children}
    </Sidebar>
  );
}

ChatSidebar.propTypes = {
  onClose: PropTypes.func,
  onScrollList: PropTypes.func,
  children: PropTypes.node,
  listRef: PropTypes.func
};

export function ChatToolbarButton(props) {
  return (
    <ToolbarButton
      {...props}
      icon={<ChatIcon />}
      preset="accent4"
      label={<FormattedMessage id="chat-toolbar-button" defaultMessage="Chat" />}
    />
  );
}

export function SoundBarAnimation({ play, onClick, containerClassName }) {
  const initialOuterSize = 45;
  const initialMiddleCircleSize = 43;
  const initialInnerCircleSize = 41;
  const initialSizeBars = 3;
  const [outerCircleSize, setOuterCircleSize] = useState(initialOuterSize);
  const [middleCircleSize, setMiddleCircleSize] = useState(initialMiddleCircleSize);
  const [innerCircleSize, setInnerCircleSize] = useState(initialInnerCircleSize);
  const [bars, setBars] = useState([initialSizeBars, initialSizeBars, initialSizeBars, initialSizeBars]);
  const intervalId = useRef(null);

  function updateBars() {
    clearInterval(intervalId.current);
    intervalId.current = setInterval(() => {
      // As we Can't use by the moment the volume of the audio
      // we have to simulate it with random values
      const barElements = [...bars]; // Create a copy of the bars array
      for (let i = 0; i < barElements.length; i++) {
        const normalizedVolume = 1 + Math.random() * 2; // Fake volume level between 0.5 and 2
        const height = Math.max(5, normalizedVolume * 5);

        barElements[i] = height;
      }
      setBars(barElements);

      const normalizeCircle = 1 + Math.random() * 2;

      const outerSize = Math.max(initialOuterSize, initialInnerCircleSize + normalizeCircle * 10);
      const middleSize = Math.max(initialMiddleCircleSize, initialInnerCircleSize + normalizeCircle * 5);
      const innerSize = Math.max(initialInnerCircleSize, initialInnerCircleSize + normalizeCircle * 3);

      setOuterCircleSize(outerSize);
      setMiddleCircleSize(middleSize);
      setInnerCircleSize(innerSize);
    }, 150);
  }

  useEffect(() => {
    return () => {
      clearInterval(intervalId.current); // Cleanup interval on component unmount
    };
  }, []);

  const resetBars = () => {
    clearInterval(intervalId.current);
    setBars([initialSizeBars, initialSizeBars, initialSizeBars, initialSizeBars]);
    setOuterCircleSize(initialOuterSize);
    setMiddleCircleSize(initialMiddleCircleSize);
    setInnerCircleSize(initialInnerCircleSize);
  };

  useEffect(() => {
    if (play) {
      updateBars();
    } else {
      resetBars();
    }
  }, [play]);

  return (
    <div className={containerClassName}>
      <div className={styles.soundbarContainer} onClick={onClick}>
        <div className="audio-circle outer-circle" style={{ width: outerCircleSize, height: outerCircleSize }}></div>
        <div className="audio-circle middle-circle" style={{ width: middleCircleSize, height: middleCircleSize }}></div>
        <div className="audio-circle inner-circle" style={{ width: innerCircleSize, height: innerCircleSize }}></div>
        <div className="audio-button">
          {play && (
            <div className="audio-button-inner" id="speaker-bot-container">
              <div id="bot-bar-1" className="audio-button-inner-element" style={{ height: bars[0] }}></div>
              <div id="bot-bar-2" className="audio-button-inner-element" style={{ height: bars[1] }}></div>
              <div id="bot-bar-3" className="audio-button-inner-element" style={{ height: bars[2] }}></div>
              <div id="bot-bar-4" className="audio-button-inner-element" style={{ height: bars[3] }}></div>
            </div>
          )}
          {!play && <PlayFilled />}
        </div>
      </div>
    </div>
  );
}
